import React, { useMemo } from 'react';
import DayCell from './DayCell';
import styles from './calendar.module.css';
import { formatTime, formatTimeRange } from '../../utils/time';
import { buildMonthGrid, toDateKey } from '../../utils/monthGrid';
import { GRID_END_MINUTES, GRID_START_MINUTES, SLOT_MINUTES } from '../../utils/availabilitySchedule';

const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK_VIEW_START_HOUR = GRID_START_MINUTES / 60;
const WEEK_VIEW_END_HOUR = GRID_END_MINUTES / 60;
const SLOT_HEIGHT = 28;
const WEEK_VIEW_HEIGHT = ((GRID_END_MINUTES - GRID_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT;

export { toDateKey };

export function getWeekStart(dateValue) {
    const base = new Date(dateValue instanceof Date ? dateValue : `${dateValue}T12:00:00`);
    const day = base.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    base.setDate(base.getDate() + diff);
    base.setHours(0, 0, 0, 0);
    return base;
}

export function getWeekDates(dateValue) {
    const start = getWeekStart(dateValue);
    return Array.from({ length: 7 }, (_, index) => {
        const next = new Date(start);
        next.setDate(start.getDate() + index);
        return next;
    });
}

export function resolveAppointmentDateKey(entry) {
    const rawValue = entry?.appointment_date ?? entry?.date ?? entry?.start_at ?? entry?.scheduled_for ?? entry?.scheduledAt;

    if (!rawValue) {
        return null;
    }

    if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (!trimmed) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            return trimmed;
        }

        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) {
            return toDateKey(parsed);
        }

        return null;
    }

    if (rawValue instanceof Date) {
        return toDateKey(rawValue);
    }

    return null;
}

function parseTimeToMinutes(value) {
    if (!value || typeof value !== 'string') return null;

    const match = value.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    return hours * 60 + minutes;
}

function getAppointmentTimeRange(appointment) {
    const startMinutes = parseTimeToMinutes(appointment?.start_time ?? appointment?.startTime ?? appointment?.time_start);
    const endMinutes = parseTimeToMinutes(appointment?.end_time ?? appointment?.endTime ?? appointment?.time_end);

    if (startMinutes === null && endMinutes === null) {
        return { startMinutes: WEEK_VIEW_START_HOUR * 60, endMinutes: WEEK_VIEW_START_HOUR * 60 + 60 };
    }

    const safeStart = startMinutes ?? (endMinutes ?? WEEK_VIEW_START_HOUR * 60) - 60;
    const safeEnd = endMinutes ?? safeStart + 60;
    return { startMinutes: safeStart, endMinutes: safeEnd };
}

function formatHourLabel(hour) {
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const normalized = hour % 12 === 0 ? 12 : hour % 12;
    return `${normalized} ${suffix}`;
}

function Calendar({ appointments = [], selectedMonth, selectedDate, onSelectDate, view = 'month', weekDates = [], onSelectAppointment = () => {} }) {
    const [year, month] = selectedMonth.split('-').map(Number);

    const appointmentsByDate = useMemo(() => {
        const map = new Map();
        appointments.forEach((entry) => {
            const dateKey = resolveAppointmentDateKey(entry);
            if (!dateKey) return;

            const existing = map.get(dateKey) ?? [];
            existing.push(entry);
            map.set(dateKey, existing);
        });
        return map;
    }, [appointments]);

    const cells = useMemo(() => buildMonthGrid(year, month - 1), [year, month]);

    if (view === 'week') {
        const days = weekDates.length ? weekDates : getWeekDates(selectedDate || new Date());
        const hours = Array.from({ length: WEEK_VIEW_END_HOUR - WEEK_VIEW_START_HOUR + 1 }, (_, index) => WEEK_VIEW_START_HOUR + index);

        return (
            <div className={`${styles.calendar} ${styles.weekCalendar}`} aria-label="Weekly appointment calendar">
                <div className={styles.weekGrid}>
                    <div className={styles.timeHeaderCell} />
                    {days.map((date) => {
                        const dateKey = toDateKey(date);
                        const isSelected = selectedDate === dateKey;
                        return (
                            <button
                                key={dateKey}
                                type="button"
                                className={`${styles.weekDayHeader} ${isSelected ? styles.weekDaySelected : ''}`}
                                aria-pressed={isSelected}
                                onClick={() => onSelectDate(dateKey)}
                            >
                                <strong>{date.getDate()}</strong>
                                <span>{WEEKDAY_LABELS[date.getDay()]}</span>
                            </button>
                        );
                    })}
                    <div className={styles.timeColumn} style={{ height: WEEK_VIEW_HEIGHT }}>
                        {hours.map((hour) => (
                            <span
                                key={hour}
                                className={styles.timeRowLabel}
                                style={{ top: ((hour * 60 - GRID_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT }}
                            >
                                {formatHourLabel(hour)}
                            </span>
                        ))}
                    </div>

                    {days.map((date) => {
                        const dateKey = toDateKey(date);
                        const dayAppointments = appointmentsByDate.get(dateKey) ?? [];

                        return (
                            <div key={dateKey} className={styles.dayColumn} style={{ height: WEEK_VIEW_HEIGHT }}>
                                {dayAppointments.map((appointment) => {
                                    const { startMinutes, endMinutes } = getAppointmentTimeRange(appointment);
                                    const clampedStart = Math.max(startMinutes, GRID_START_MINUTES);
                                    const clampedEnd = Math.min(endMinutes, GRID_END_MINUTES);
                                    if (clampedEnd <= clampedStart) return null;
                                    const top = ((clampedStart - GRID_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT;
                                    const height = Math.min(
                                        Math.max(((clampedEnd - clampedStart) / SLOT_MINUTES) * SLOT_HEIGHT, SLOT_HEIGHT),
                                        WEEK_VIEW_HEIGHT - top,
                                    );
                                    const title = appointment?.customer?.name || appointment?.customer_name || 'Appointment';
                                    const timeText = appointment?.start_time
                                        ? formatTimeRange(appointment.start_time, appointment.end_time)
                                        : formatTime(appointment?.time) || 'Scheduled';

                                    return (
                                        <button
                                            key={`${dateKey}-${appointment.id ?? title}-${startMinutes}-${endMinutes}`}
                                            type="button"
                                            className={styles.appointmentBlock}
                                            style={{ top: `${top}px`, height: `${height}px` }}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onSelectAppointment(appointment);
                                            }}
                                        >
                                            <span className={styles.appointmentTitle}>{title}</span>
                                            <span className={styles.appointmentMeta}>{timeText}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.calendar}>
            <div className={styles.grid}>
                {WEEKDAY_LABELS.map((label) => (
                    <div key={label} className={styles.weekdayLabel}>{label}</div>
                ))}
                {cells.map(({ date, isCurrentMonth }) => {
                    const dateKey = toDateKey(date);
                    const dayAppointments = appointmentsByDate.get(dateKey) ?? [];
                    const hasAppointments = isCurrentMonth && dayAppointments.length > 0;

                    return (
                        <DayCell
                            key={dateKey}
                            date={date}
                            isCurrentMonth={isCurrentMonth}
                            hasAppointment={hasAppointments}
                            isSelected={selectedDate === dateKey}
                            onSelect={() => onSelectDate(dateKey, dayAppointments[0] ? (dayAppointments[0].id ?? dayAppointments[0].hashed_appointment_id ?? `${dateKey}-${dayAppointments[0].customer?.name || dayAppointments[0].customer_name || 'appointment'}`) : null)}
                        />
                    );
                })}
            </div>
        </div>
    );
}

export default Calendar;
